import React from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight,
  FileDown,
  Calendar,
  Sparkles,
  ShieldCheck,
  MapPin,
  CheckCircle,
} from 'lucide-react';
import { PublicHeader } from '../components/PublicHeader';
import { SelfGuidedTour } from '../components/SelfGuidedTour';
import { useForceLightMode } from '../hooks/useForceLightMode';

const trackEvent = (event: string) => {
  if (typeof window !== 'undefined' && typeof window.plausible === 'function') {
    window.plausible(event);
  }
};

const WHITEPAPER_URL = '/clinlexis-whitepaper-medical-record-review.pdf';

export const ConferencePage: React.FC = () => {
  useForceLightMode();

  const handleWhitepaperClick = () => {
    trackEvent('conference_whitepaper_download');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-medical-50 via-white to-primary-50/30">
      <PublicHeader />

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        {/* Hero */}
        <div className="text-center mb-14 max-w-3xl mx-auto">
          <span className="inline-block px-3 py-1 mb-5 rounded-full bg-amber-50 border border-amber-200 text-[11px] font-semibold tracking-widest uppercase text-amber-700">
            Conference Exclusive
          </span>
          <h1 className="text-4xl md:text-5xl font-bold text-medical-900 mb-5 leading-tight tracking-tight">
            Try ClinLexis on your next case
          </h1>
          <p className="text-xl text-medical-600 leading-relaxed font-light">
            Three ways to get started — pick whichever suits you. Reserved for delegates of the conferences we attend.
          </p>
        </div>

        {/* CTA Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-16">
          {/* 1. Whitepaper */}
          <a
            href={WHITEPAPER_URL}
            onClick={handleWhitepaperClick}
            target="_blank"
            rel="noopener noreferrer"
            className="group bg-white rounded-2xl shadow-lg border border-medical-200/60 p-7 flex flex-col hover:shadow-xl hover:border-primary-200 transition-all"
          >
            <div className="w-12 h-12 bg-primary-50 rounded-xl flex items-center justify-center mb-5">
              <FileDown className="h-6 w-6 text-primary-600" />
            </div>
            <h2 className="text-lg font-semibold text-medical-900 mb-2">
              Read the whitepaper
            </h2>
            <p className="text-sm text-medical-600 mb-6 flex-1">
              How AI-assisted chronologies and bundle review change the economics of medico-legal case preparation.
            </p>
            <span className="inline-flex items-center text-primary-600 font-medium text-sm group-hover:translate-x-0.5 transition-transform">
              Download PDF
              <ArrowRight className="h-4 w-4 ml-1.5" />
            </span>
          </a>

          {/* 2. Watch the Demo */}
          <Link
            to="/demo?source=conference"
            onClick={() => trackEvent('conference_book_demo_click')}
            className="group bg-white rounded-2xl shadow-lg border border-medical-200/60 p-7 flex flex-col hover:shadow-xl hover:border-primary-200 transition-all"
          >
            <div className="w-12 h-12 bg-primary-50 rounded-xl flex items-center justify-center mb-5">
              <Calendar className="h-6 w-6 text-primary-600" />
            </div>
            <h2 className="text-lg font-semibold text-medical-900 mb-2">
              Watch the Demo
            </h2>
            <p className="text-sm text-medical-600 mb-6 flex-1">
              A 30-minute walkthrough tailored to your practice — clinical negligence, personal injury, or expert work.
            </p>
            <span className="inline-flex items-center text-primary-600 font-medium text-sm group-hover:translate-x-0.5 transition-transform">
              Pick a time
              <ArrowRight className="h-4 w-4 ml-1.5" />
            </span>
          </Link>

          {/* 3. Sign up — highlighted */}
          <Link
            to="/signup?cohort=conference"
            onClick={() => trackEvent('conference_signup_click')}
            className="group relative bg-gradient-to-br from-primary-600 to-primary-700 text-white rounded-2xl shadow-xl border-2 border-primary-600 p-7 flex flex-col hover:shadow-2xl hover:-translate-y-0.5 transition-all"
          >
            <div className="absolute -top-3 left-7 px-2.5 py-0.5 rounded-full bg-amber-400 text-amber-900 text-[10px] font-bold tracking-widest uppercase">
              Best value
            </div>
            <div className="w-12 h-12 bg-white/15 rounded-xl flex items-center justify-center mb-5">
              <Sparkles className="h-6 w-6 text-white" />
            </div>
            <h2 className="text-lg font-semibold mb-2">
              Sign up — 1,500 free pages
            </h2>
            <p className="text-sm text-primary-100 mb-6 flex-1">
              Conference delegates get 50% more free pages on sign-up — 1,500 instead of 1,000. No code needed.
            </p>
            <span className="inline-flex items-center text-white font-medium text-sm group-hover:translate-x-0.5 transition-transform">
              Claim offer
              <ArrowRight className="h-4 w-4 ml-1.5" />
            </span>
          </Link>
        </div>

        {/* Self-guided tour */}
        <section className="max-w-3xl mx-auto mb-16">
          <SelfGuidedTour />
        </section>

        {/* Why ClinLexis */}
        <div className="bg-white rounded-2xl border border-medical-200/60 p-8 md:p-10 mb-10">
          <h3 className="text-lg font-semibold text-medical-900 mb-6 text-center">
            Why solicitors and expert witnesses choose ClinLexis
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              {
                title: 'Hours back, per case',
                body: 'AI-drafted chronologies, bundle review, and citation lookup — reviewed and signed off by you.',
              },
              {
                title: 'Built for the courts',
                body: 'CPR-aware drafting, neutral language, and citations that hold up under cross-examination.',
              },
              {
                title: 'Compliant by design',
                body: 'NHS DSPT compliant, UK data residency, and documents that never leave the UK.',
              },
            ].map((item, i) => (
              <div key={i} className="flex items-start space-x-3">
                <CheckCircle className="h-5 w-5 text-emerald-500 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-semibold text-medical-900 text-sm mb-1">{item.title}</p>
                  <p className="text-sm text-medical-600 leading-relaxed">{item.body}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Trust signals */}
        <div className="flex flex-wrap justify-center gap-3">
          {[
            { icon: ShieldCheck, label: 'NHS DSPT Compliant' },
            { icon: MapPin, label: 'UK Data Residency' },
          ].map((badge, i) => (
            <span
              key={i}
              className="inline-flex items-center space-x-1.5 px-3 py-1.5 bg-green-50 border border-green-200 rounded-full text-xs font-medium text-green-700"
            >
              <badge.icon className="h-3 w-3" />
              <span>{badge.label}</span>
            </span>
          ))}
        </div>
      </div>

      <footer className="border-t border-medical-200 mt-8 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-2">
          <p className="text-sm text-medical-400 text-center">
            &copy; {new Date().getFullYear()} ClinLexis&trade;. All rights reserved.
          </p>
          <p className="text-xs text-medical-500 text-center">
            ClinLexis Ltd &middot; Company No. 17081483 &middot; Registered in England and Wales
          </p>
        </div>
      </footer>
    </div>
  );
};
